import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { DialogueLine, StoryQuest, WorldObject } from '@mezha/api'
import { useStoryInteractMutation, useStoryQuery } from '../../api/endpoints/quests'
import { useAppDispatch } from '../../app/store'
import { ui } from '../../app/uiSlice'
import { useCommand } from '../../lib/useCommand'
import { useShellMode } from '../../shell/ShellContext'
import { Emblem } from '../../ui/brand/Emblems'
import { ActionButton, cx } from '../../ui'
import { CloseButton } from '../../ui/CloseButton'

type Props = { quest: StoryQuest; object: WorldObject; onClose: () => void }

/** A conversation of the current story step: lines one by one, then the
 *  answers. A pressed answer shows «Сказать» in its row; a second press sends it. */
export function Dialog({ quest, object, onClose }: Props) {
  useShellMode('dialog', 'scene')
  const step = quest.current
  const story = useStoryQuery()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [interact] = useStoryInteractMutation()
  const cmd = useCommand()
  const [index, setIndex] = useState(0)
  const [choice, setChoice] = useState<string | null>(null)

  const lines: DialogueLine[] = step && step.dialogue.length > 0
    ? step.dialogue
    : [{ speaker: object.character_id, speaker_name: object.name, portrait: object.portrait, text: step?.text ?? '' }]
  const line = lines[Math.min(index, lines.length - 1)]
  const last = index >= lines.length - 1
  const choosing = last && step?.kind === 'choice'

  // The step moved on elsewhere (another tab, an event): the conversation is over.
  useEffect(() => {
    if (story.data && !story.data.quests.some(q => q.id === quest.id && q.current?.id === step?.id)) onClose()
  }, [story.data, quest.id, step?.id, onClose])

  const [sending, setSending] = useState(false)
  const send = async () => {
    if (!step || sending) return
    setSending(true)
    const r = await cmd.run(() => interact({ quest_id: quest.id, object_id: object.id, choice_id: choice ?? undefined }).unwrap())
    setSending(false)
    if (!r) {
      void story.refetch()
      return
    }
    if (r.battle_id) return navigate(`/play/battle/${r.battle_id}`, { state: { from: '/play' } })
    dispatch(ui.actions.notice(step.kind === 'choice' ? 'Решение записано в журнал' : `Записано в журнал: ${step.title}`))
    onClose()
  }

  const next = () => setIndex(i => Math.min(i + 1, lines.length - 1))
  const say = (id: string) => (choice === id ? send() : setChoice(id))

  // Keyboard: space — next line, digits — pick an answer, Enter — say the picked one.
  const keys = useRef<(e: KeyboardEvent) => void>(() => {})
  keys.current = e => {
    if (e.target instanceof HTMLElement && e.target.closest('input, textarea')) return
    if (e.key === ' ' && !last && !(e.target instanceof HTMLButtonElement)) { e.preventDefault(); next() }
    const n = Number(e.key)
    if (choosing && step && n >= 1 && n <= step.choices.length) setChoice(step.choices[n - 1].id)
    if (e.key === 'Enter' && choosing && choice && !(e.target instanceof HTMLButtonElement)) void send()
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keys.current(e)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  let action: ReactNode = null
  if (!last) action = <ActionButton gold className="dialog__next" onPress={next}>Дальше ▸</ActionButton>
  else if (step?.kind === 'battle' && step.battle_id) {
    const id = step.battle_id
    action = <ActionButton gold className="dialog__next" onPress={() => navigate(`/play/battle/${id}`, { state: { from: '/play' } })}><Emblem name="act.strike" size={16} />К бою</ActionButton>
  } else if (step?.kind === 'battle') action = <ActionButton gold className="dialog__next" onPress={send}><Emblem name="act.strike" size={16} />{step.tutorial ? 'В бой · учебный' : 'В бой'}</ActionButton>
  else if (!choosing) action = <ActionButton gold className="dialog__next" onPress={send}>Продолжить ▸</ActionButton>

  const player = line.speaker === 'player'
  const portrait = player ? '' : line.portrait || object.portrait
  const speaker = player ? 'Вы' : line.speaker_name || object.name
  return (
    <div className="dialog-screen">
      <div className="speaker" aria-hidden={!portrait}>
        <div className="speaker__arch">{portrait ? <img src={portrait} alt={speaker} /> : <Emblem name="nav.hero" size={120} />}</div>
      </div>
      <section className="dialog iron wood" aria-labelledby="dialog-name">
        <header className="dialog__header">
          <div className="dialog__name plank"><h2 className="plank__in" id="dialog-name">{speaker}{quest.title && <small> · {quest.title}</small>}</h2></div>
          <CloseButton className="window__close dialog__close" onClose={onClose} label="Отложить разговор" />
        </header>
        <div className={cx('dialog__line parch', !last && 'is-next')} aria-live="polite" onClick={last ? undefined : next}><p>{line.text}</p></div>
        {choosing && (
          <ul className="choices" aria-label="Ответы">
            {step.choices.map((c, i) => (
              <li key={c.id}>
                <button type="button" className={cx('choice-row', choice === c.id && 'is-selected')} aria-pressed={choice === c.id}
                  aria-label={choice === c.id ? `Сказать: ${c.text}` : c.text} disabled={sending} onClick={() => void say(c.id)}>
                  <span className="gem gem--gold" aria-hidden="true">{i + 1}</span><b>{c.text}</b>
                  {choice === c.id && <span className="choice-row__go" aria-hidden="true">{sending ? 'Отвечаем…' : 'Сказать ▸'}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="dialog__foot">
          {choosing && <span className="warn">◆ Решение записывается в журнал и не меняется.</span>}
          {!last && <span className="dialog__count">{index + 1} из {lines.length}</span>}
          {cmd.text && <span className="dialog__error" role="alert">{cmd.text}</span>}
          {action}
        </div>
      </section>
    </div>
  )
}
